import * as Sentry from '@sentry/react'
import { browserTracingIntegration } from '@sentry/react'
import {
  AnyRouter,
  ErrorComponentProps,
  RouterProvider,
  useNavigate,
} from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { ErrorScreen } from 'components/ErrorScreen'
import mixpanel from 'mixpanel-browser'
import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { styled } from 'styled-components'
import { useSWRConfig } from 'swr'

const StyledErrorPageLayout = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.forecast.background3};
`

const StyledGoBackButton = styled.button`
  color: ${({ theme }) => theme.forecast.text1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background-color: ${({ theme }) => theme.forecast.background4};
  padding: 4px 8px;
`

// TODO: move this to a "page"
export function ErrorFallback({ error }: ErrorComponentProps) {
  const navigate = useNavigate()

  let errorMessage = 'Unknown error'

  if (error instanceof Error) errorMessage = error.message
  else if (typeof error === 'string') errorMessage = error

  return (
    <StyledErrorPageLayout>
      <ErrorScreen title='There was an error' message={errorMessage}>
        <StyledGoBackButton onClick={() => navigate({ to: '/' })}>
          Go back
        </StyledGoBackButton>
      </ErrorScreen>
    </StyledErrorPageLayout>
  )
}

export function App<ROUTER_TYPE extends AnyRouter>(props: {
  router: ROUTER_TYPE
}) {
  const { router } = props
  const { ENVIRONMENT, MIXPANEL_AUTH_TOKEN, SENTRY_DSN } = useRuntimeVariables()

  if (import.meta.env.MODE === 'production' && SENTRY_DSN !== '') {
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
        }),
        Sentry.replayCanvasIntegration(),
      ],
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 1.0,
      sendDefaultPii: true,
      environment: ENVIRONMENT,
      tracesSampleRate: 0.25,
    })
  }

  mixpanel.init(MIXPANEL_AUTH_TOKEN, {
    persistence: 'localStorage',
    track_pageview: 'url-with-path-and-query-string',
  })
  mixpanel.register({ app: 'Forecast' })

  const { cache } = useSWRConfig()

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <ErrorFallback reset={() => {}} error={error} />
      )}>
      <RouterProvider router={router} context={{ cache }} />
    </ErrorBoundary>
  )
}

export default App
